import { z } from 'zod';

export type PrV2Reviewer = {
  login: string;
  avatarUrl: string | null;
};

export type PrV2Label = {
  name: string;
  color: string | null;
};

export type PrV2RequestedTeam = {
  name: string;
  // Slug is what the GitHub API takes for team reviewer mutations. Null for
  // metadata stored before slugs were preserved.
  slug: string | null;
};

export type PrV2MetadataSummary = {
  body: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  mergeableState: string | null;
  mergeable: boolean | null;
  headRef: string | null;
  baseRef: string | null;
  changedFiles: number | null;
  additions: number | null;
  deletions: number | null;
  commits: number | null;
  comments: number | null;
  reviewComments: number | null;
  reviewers: PrV2Reviewer[];
  requestedTeams: PrV2RequestedTeam[];
  labels: PrV2Label[];
  commitsCount: number | null;
  reviewsCount: number | null;
  issueCommentsCount: number | null;
  reviewCommentsCount: number | null;
  checkRunsCount: number | null;
};

const prV2ReviewerSchema = z.object({
  login: z.string(),
  avatar_url: z.string().nullable().optional(),
});

const prV2RequestedTeamSchema = z.object({
  name: z.string(),
  slug: z.string().nullable().optional(),
});

const prV2LabelSchema = z.object({
  name: z.string(),
  color: z.string().nullable().optional(),
});

const nullableStringSchema = z.string().nullable();
const nullableBooleanSchema = z.boolean().nullable();
const nullableFiniteNumberSchema = z.number().finite().nullable();

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseNullableField<T>(value: unknown, schema: z.ZodType<T>): T | null {
  const parsed = schema.safeParse(value);
  return parsed.success ? (parsed.data ?? null) : null;
}

function parseCollection<T>(value: unknown, schema: z.ZodType<T>): T[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    const parsed = schema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  });
}

export function parsePrV2Metadata(metadata: unknown): PrV2MetadataSummary {
  const root = asRecord(metadata);
  const pullRequest = asRecord(root?.pullRequest);
  const head = asRecord(pullRequest?.head);
  const base = asRecord(pullRequest?.base);
  const reviewers = parseCollection(pullRequest?.requested_reviewers, prV2ReviewerSchema);
  const requestedTeams = parseCollection(pullRequest?.requested_teams, prV2RequestedTeamSchema);
  const labels = parseCollection(pullRequest?.labels, prV2LabelSchema);

  return {
    body: parseNullableField(pullRequest?.body, nullableStringSchema),
    createdAt: parseNullableField(pullRequest?.created_at, nullableStringSchema),
    updatedAt: parseNullableField(pullRequest?.updated_at, nullableStringSchema),
    mergeableState: parseNullableField(pullRequest?.mergeable_state, nullableStringSchema),
    mergeable: parseNullableField(pullRequest?.mergeable, nullableBooleanSchema),
    headRef: parseNullableField(head?.ref, nullableStringSchema),
    baseRef: parseNullableField(base?.ref, nullableStringSchema),
    changedFiles: parseNullableField(pullRequest?.changed_files, nullableFiniteNumberSchema),
    additions: parseNullableField(pullRequest?.additions, nullableFiniteNumberSchema),
    deletions: parseNullableField(pullRequest?.deletions, nullableFiniteNumberSchema),
    commits: parseNullableField(pullRequest?.commits, nullableFiniteNumberSchema),
    comments: parseNullableField(pullRequest?.comments, nullableFiniteNumberSchema),
    reviewComments: parseNullableField(pullRequest?.review_comments, nullableFiniteNumberSchema),
    reviewers: reviewers.map((reviewer) => ({
      login: reviewer.login,
      avatarUrl: reviewer.avatar_url ?? null,
    })),
    requestedTeams: requestedTeams.map((team) => ({
      name: team.name,
      slug: team.slug ?? null,
    })),
    labels: labels.map((label) => ({
      name: label.name,
      color: label.color ?? null,
    })),
    commitsCount: parseNullableField(root?.commitsCount, nullableFiniteNumberSchema),
    reviewsCount: parseNullableField(root?.reviewsCount, nullableFiniteNumberSchema),
    issueCommentsCount: parseNullableField(root?.issueCommentsCount, nullableFiniteNumberSchema),
    reviewCommentsCount: parseNullableField(root?.reviewCommentsCount, nullableFiniteNumberSchema),
    checkRunsCount: parseNullableField(root?.checkRunsCount, nullableFiniteNumberSchema),
  };
}
